/**
 * Add-child dialog. Validates against the core zod schema before calling the API.
 */
import { useState } from 'react';
import { createChildInputSchema } from '@solandia/core';
import { useI18n } from '../../i18n';
import { useApi } from '../../api/ApiProvider';
import { useFamily } from '../../family/FamilyContext';
import { Dialog, Field, Form } from '../../components/ui';

const EMOJI_CHOICES = ['🐥', '🦊', '🐰', '🐼', '🐨', '🦄', '🐯', '🐸', '⭐', '🌈'];

export function AddChildDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useI18n();
  const api = useApi();
  const { family } = useFamily();
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState(EMOJI_CHOICES[0]!);
  const [birthDate, setBirthDate] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function submit() {
    const parsed = createChildInputSchema.safeParse({
      name: name.trim(),
      avatarEmoji: emoji,
      birthDate,
    });
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === 'string' && !fieldErrors[key]) {
          fieldErrors[key] = t.common.required;
        }
      }
      setErrors(fieldErrors);
      return;
    }
    setBusy(true);
    setErrors({});
    try {
      await api.createChild(family.id, parsed.data);
      onCreated();
    } catch {
      setErrors({ name: t.errors.generic });
      setBusy(false);
    }
  }

  return (
    <Dialog title={t.children.addTitle} onClose={onClose} closeLabel={t.common.close}>
      <Form onSubmit={submit}>
        <Field label={t.children.nameLabel} error={errors.name}>
          {({ id, invalid }) => (
            <input
              id={id}
              className="input"
              value={name}
              aria-invalid={invalid}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.children.namePlaceholder}
              autoFocus
            />
          )}
        </Field>

        <Field label={t.children.emojiLabel}>
          {() => (
            <div className="row wrap" role="radiogroup" aria-label={t.children.emojiLabel}>
              {EMOJI_CHOICES.map((e) => (
                <button
                  key={e}
                  type="button"
                  className="pill"
                  aria-pressed={emoji === e}
                  onClick={() => setEmoji(e)}
                  style={{ fontSize: '1.3rem', padding: '6px 12px' }}
                >
                  {e}
                </button>
              ))}
            </div>
          )}
        </Field>

        <Field label={t.children.birthDateLabel} error={errors.birthDate}>
          {({ id, invalid }) => (
            <input
              id={id}
              type="date"
              className="input"
              value={birthDate}
              aria-invalid={invalid}
              onChange={(e) => setBirthDate(e.target.value)}
            />
          )}
        </Field>

        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t.common.cancel}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? t.common.saving : t.common.add}
          </button>
        </div>
      </Form>
    </Dialog>
  );
}
